import type {
  OrderLineItem,
  ReturnRequest,
  ReturnRequestLineItem,
} from "../types";

// `quantity_bought` on the order is Shopify's returnable quantity, read live
// from `returnableFulfillments`. It already excludes units held by an open
// Shopify return, so subtracting every Baback return from it double-counts.
//
// It is still not enough on its own:
// - a return that never reached Shopify (no `shopifyReturnId`) is unknown to
//   Shopify, so its units are subtracted here;
// - a Shopify return can be cancelled while the Baback return stays active,
//   and Shopify then offers those units again. Each Baback return snapshots
//   the returnable quantity at its creation (`quantity_bought` on its line), so
//   that snapshot minus the units claimed by it and every later active return
//   caps what is left, whatever Shopify says now.
export function getReturnableQuantity(
  lineItem: OrderLineItem,
  returnRequests: ReturnRequest[],
): number {
  const claims = collectActiveClaims(returnRequests, lineItem.line_item_id);
  const shopifyRemaining =
    (lineItem.quantity_bought ?? 0) - sumUnsynced(claims);
  const ceilings = claims.map((claim) => claimCeiling(claim, claims));

  return Math.max(0, Math.min(shopifyRemaining, ...ceilings));
}

export function filterReturnableItems(
  lineItems: OrderLineItem[],
  returnRequests: ReturnRequest[],
): OrderLineItem[] {
  return lineItems.filter(
    (lineItem) => getReturnableQuantity(lineItem, returnRequests) > 0,
  );
}

type Claim = {
  createdAt: number;
  synced: boolean;
  snapshot: number;
  quantity: number;
};

function collectActiveClaims(
  returnRequests: ReturnRequest[],
  lineItemId: string,
): Claim[] {
  return returnRequests
    .filter(isActive)
    .flatMap((rr) =>
      rr.line_items
        .filter((li) => li.line_item_id === lineItemId)
        .map((li) => ({
          createdAt: rr.createdAt,
          synced: Boolean(rr.shopifyReturnId),
          snapshot: li.quantity_bought,
          quantity: claimedQuantity(li),
        })),
    )
    .filter((claim) => claim.quantity > 0);
}

// Units the merchant rejected go back to the customer, who may return them
// again: they stop being claimed by this return. Same subtraction as the
// backend guard (`PricingComputationService.getQuantityHandledMerchant` with
// status `rejected`), so the portal offers exactly what creation accepts.
function claimedQuantity(lineItem: ReturnRequestLineItem): number {
  const rejected = lineItem.merchant_informations
    .filter((info) => info.status === "rejected")
    .reduce((total, info) => total + info.quantity, 0);

  return Math.max(0, lineItem.quantity - rejected);
}

// Same rule as the backend guard on creation (CheckItemsReturnableService):
// a whole return can be rejected, and a single line can be rejected inside an
// accepted one (see `claimedQuantity`).
function isActive(returnRequest: ReturnRequest): boolean {
  const { cancelled, approved } = returnRequest.status;
  return !cancelled.done && approved.status !== "rejected";
}

function sumUnsynced(claims: Claim[]): number {
  return claims
    .filter((claim) => !claim.synced)
    .reduce((total, claim) => total + claim.quantity, 0);
}

function claimCeiling(claim: Claim, claims: Claim[]): number {
  const claimedSince = claims
    .filter((other) => other.createdAt >= claim.createdAt)
    .reduce((total, other) => total + other.quantity, 0);

  return claim.snapshot - claimedSince;
}

// The backend collapses every article-level exclusion into the single
// `returnable` boolean (`ReturnRequestLineItemsService.isLineItemReturnable`):
// non-returnable product tag, metafield, custom attribute, override rule and an
// elapsed return window. It carries no reason, so the portal can only state that
// the article cannot be returned.
//
// Both lists are drawn from the same quantity-filtered set, so an article that
// is already fully returned stays out of the selection instead of reappearing as
// a disabled row.
export function splitByReturnability(
  lineItems: OrderLineItem[],
  returnRequests: ReturnRequest[],
): { returnable: OrderLineItem[]; nonReturnable: OrderLineItem[] } {
  const available = filterReturnableItems(lineItems, returnRequests);

  return {
    returnable: available.filter((lineItem) => lineItem.returnable),
    nonReturnable: available.filter((lineItem) => !lineItem.returnable),
  };
}
